import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAuth } from "../../hooks/useAuth";
import { useAlert } from "../../contexts/AlertContext";
import { authAPI } from "../../services/api";
import {
  MdPerson,
  MdEmail,
  MdSecurity,
  MdVerifiedUser,
  MdEdit,
  MdSave,
  MdCancel,
  MdLock,
} from "react-icons/md";

const Profile = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const { addAlert } = useAlert();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [activeTab, setActiveTab] = useState("profile");

  const PersonIcon = MdPerson as React.FC<React.SVGProps<SVGSVGElement>>;
  const EmailIcon = MdEmail as React.FC<React.SVGProps<SVGSVGElement>>;
  const SecurityIcon = MdSecurity as React.FC<React.SVGProps<SVGSVGElement>>;
  const VerifiedIcon = MdVerifiedUser as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const EditIcon = MdEdit as React.FC<React.SVGProps<SVGSVGElement>>;
  const SaveIcon = MdSave as React.FC<React.SVGProps<SVGSVGElement>>;
  const CancelIcon = MdCancel as React.FC<React.SVGProps<SVGSVGElement>>;
  const LockIcon = MdLock as React.FC<React.SVGProps<SVGSVGElement>>;

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        username: user.username || "",
        email: user.email || "",
      });
    }
  }, [user]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await authAPI.updateProfile(formData);
      updateUser(response.data);
      await refreshUser();
      addAlert({ type: "success", message: "Profile updated successfully!" });
      setIsEditing(false);
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to update profile",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      addAlert({ type: "error", message: "New passwords do not match" });
      setIsSubmitting(false);
      return;
    }

    try {
      await authAPI.updatePassword({
        current_password: passwordData.current_password,
        password: passwordData.new_password,
        password_confirmation: passwordData.new_password_confirmation,
      });

      addAlert({ type: "success", message: "Password changed successfully!" });
      setPasswordData({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to change password",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        username: user.username || "",
        email: user.email || "",
      });
    }
    setIsEditing(false);
  };

  const handleResendVerification = async () => {
    try {
      await authAPI.resendVerification();
      addAlert({ type: "success", message: "Verification email sent!" });
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to send verification email" });
    }
  };

  if (!user) {
    return (
      <div className={GlobalStyles.containerCenter}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className={`${CommonClasses.textGray} mt-4`}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Profile Settings
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Manage your account information and security settings.
          </p>
        </div>
        {!isEditing && activeTab === "profile" && (
          <button
            onClick={() => setIsEditing(true)}
            className={`${GlobalStyles.buttonIconOutline}`}
          >
            <EditIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className={`${GlobalStyles.sectionWrap} p-0`}>
        <nav className="-mb-px flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab("profile")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "profile"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <PersonIcon className="w-4 h-4 inline mr-2" />
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab("security")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "security"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <SecurityIcon className="w-4 h-4 inline mr-2" />
            Security
          </button>
          <button
            onClick={() => setActiveTab("verification")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "verification"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <VerifiedIcon className="w-4 h-4 inline mr-2" />
            Verification
          </button>
        </nav>
      </div>

      {/* Profile Information Tab */}
      {activeTab === "profile" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className={`${GlobalStyles.sectionWrap}`}>
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
              >
                Basic Information
              </h2>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={GlobalStyles.formGroup}>
                    <label htmlFor="first_name" className={GlobalStyles.label}>
                      First Name
                    </label>
                    <div className={GlobalStyles.inputWrapper}>
                      <PersonIcon className={GlobalStyles.inputIcon} />
                      <input
                        id="first_name"
                        name="first_name"
                        type="text"
                        value={formData.first_name}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`${GlobalStyles.input} ${
                          !isEditing ? "bg-gray-100 dark:bg-gray-700" : ""
                        }`}
                        required
                      />
                    </div>
                  </div>
                  <div className={GlobalStyles.formGroup}>
                    <label htmlFor="last_name" className={GlobalStyles.label}>
                      Last Name
                    </label>
                    <div className={GlobalStyles.inputWrapper}>
                      <PersonIcon className={GlobalStyles.inputIcon} />
                      <input
                        id="last_name"
                        name="last_name"
                        type="text"
                        value={formData.last_name}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`${GlobalStyles.input} ${
                          !isEditing ? "bg-gray-100 dark:bg-gray-700" : ""
                        }`}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className={GlobalStyles.formGroup}>
                  <label htmlFor="username" className={GlobalStyles.label}>
                    Username
                  </label>
                  <div className={GlobalStyles.inputWrapper}>
                    <PersonIcon className={GlobalStyles.inputIcon} />
                    <input
                      id="username"
                      name="username"
                      type="text"
                      value={formData.username}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      className={`${GlobalStyles.input} ${
                        !isEditing ? "bg-gray-100 dark:bg-gray-700" : ""
                      }`}
                      required
                    />
                  </div>
                </div>

                <div className={GlobalStyles.formGroup}>
                  <label htmlFor="email" className={GlobalStyles.label}>
                    Email Address
                  </label>
                  <div className={GlobalStyles.inputWrapper}>
                    <EmailIcon className={GlobalStyles.inputIcon} />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      className={`${GlobalStyles.input} ${
                        !isEditing ? "bg-gray-100 dark:bg-gray-700" : ""
                      }`}
                      required
                    />
                  </div>
                </div>

                {isEditing && (
                  <div className="flex gap-2 pt-4 max-sm:flex-col">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className={`${GlobalStyles.buttonOutline} flex-1`}
                    >
                      <CancelIcon className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`${GlobalStyles.buttonPrimary} flex-1 ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <SaveIcon className="w-4 h-4 mr-2" />
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Account Status */}
          <div className="space-y-6">
            <div className={`${GlobalStyles.sectionWrap}`}>
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
              >
                Account Status
              </h2>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-3">
                    <PersonIcon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-800 dark:text-gray-200">
                    {user.first_name} {user.last_name}
                  </h3>
                  <p className={`${CommonClasses.textGray} mb-2`}>
                    @{user.username}
                  </p>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Member since{" "}
                    {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      Account Level:
                    </span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      Level {user.account_level}
                    </span>
                  </div>
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      KYC Status:
                    </span>
                    <span
                      className={`font-medium ${
                        user.kyc_status === "verified"
                          ? "text-green-600"
                          : user.kyc_status === "pending"
                          ? "text-yellow-600"
                          : "text-red-600"
                      }`}
                    >
                      {user.kyc_status?.toUpperCase() || "NOT SUBMITTED"}
                    </span>
                  </div>
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      Role:
                    </span>
                    <span className="font-medium text-gray-800 dark:text-gray-200 capitalize">
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Balances */}
            <div className={`${GlobalStyles.sectionWrap}`}>
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
              >
                Account Balances
              </h2>
              <div className="space-y-3">
                <div
                  className={`${CommonClasses.flexBetween} p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800`}
                >
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    Ledger Balance
                  </span>
                  <span className="font-bold text-blue-600">
                    ${Number(user.ledger_balance).toFixed(2)}
                  </span>
                </div>
                <div
                  className={`${CommonClasses.flexBetween} p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800`}
                >
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    Stake Balance
                  </span>
                  <span className="font-bold text-green-600">
                    ${Number(user.stake_balance).toFixed(2)}
                  </span>
                </div>
                <div
                  className={`${CommonClasses.flexBetween} p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800`}
                >
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    Bonus Balance
                  </span>
                  <span className="font-bold text-purple-600">
                    ${Number(user.bonus_balance).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === "security" && (
        <div className={`${GlobalStyles.sectionWrap}`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Security Settings
          </h2>

          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {/* Current Password */}
            <div className={GlobalStyles.formGroup}>
              <label htmlFor="current_password" className={GlobalStyles.label}>
                Current Password
              </label>
              <div className={GlobalStyles.inputWrapper}>
                <LockIcon className={GlobalStyles.inputIcon} />
                <input
                  id="current_password"
                  name="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  className={GlobalStyles.input}
                  required
                  placeholder="Enter your current password"
                />
              </div>
            </div>

            {/* New Password */}
            <div className={GlobalStyles.formGroup}>
              <label htmlFor="new_password" className={GlobalStyles.label}>
                New Password
              </label>
              <div className={GlobalStyles.inputWrapper}>
                <LockIcon className={GlobalStyles.inputIcon} />
                <input
                  id="new_password"
                  name="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  className={GlobalStyles.input}
                  required
                  minLength={6}
                  placeholder="Enter new password"
                />
              </div>
            </div>

            {/* Confirm New Password */}
            <div className={GlobalStyles.formGroup}>
              <label
                htmlFor="new_password_confirmation"
                className={GlobalStyles.label}
              >
                Confirm New Password
              </label>
              <div className={GlobalStyles.inputWrapper}>
                <LockIcon className={GlobalStyles.inputIcon} />
                <input
                  id="new_password_confirmation"
                  name="new_password_confirmation"
                  type="password"
                  value={passwordData.new_password_confirmation}
                  onChange={handlePasswordChange}
                  className={GlobalStyles.input}
                  required
                  minLength={6}
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`${GlobalStyles.buttonPrimary} ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <LockIcon className="w-4 h-4 mr-2" />
              {isSubmitting ? "Updating Password..." : "Change Password"}
            </button>
          </form>

          {/* Security Status */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              Security Status
            </h3>
            <div className="space-y-3">
              <div
                className={`${CommonClasses.flexBetween} p-3 border border-gray-200 dark:border-gray-700 rounded-lg`}
              >
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <EmailIcon className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-200">
                      Email Verification
                    </p>
                    <p
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      {user.email_verified_at ? "Verified" : "Not verified"}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.email_verified_at
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                  }`}
                >
                  {user.email_verified_at ? "VERIFIED" : "PENDING"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Tab */}
      {activeTab === "verification" && (
        <div className="space-y-6">
          {/* Email Verification */}
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              Email Verification
            </h2>
            <div
              className={`${CommonClasses.flexBetween} p-4 border border-gray-200 dark:border-gray-700 rounded-lg max-sm:flex-col max-sm:items-start max-sm:gap-4`}
            >
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <EmailIcon className="w-8 h-8 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Email Address Verification
                  </p>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    {user.email_verified_at
                      ? "Your email address has been successfully verified."
                      : "Please verify your email address to access all features."}
                  </p>
                </div>
              </div>
              <div className="text-right max-sm:text-left max-sm:w-full">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    user.email_verified_at
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                  }`}
                >
                  {user.email_verified_at ? "VERIFIED" : "PENDING"}
                </span>
                {!user.email_verified_at && (
                  <button
                    onClick={handleResendVerification}
                    className={`${GlobalStyles.buttonPrimary} !text-xs mt-2 max-sm:w-full`}
                  >
                    Verify
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* KYC Verification */}
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              KYC Verification
            </h2>
            <div className="space-y-4">
              <div
                className={`${CommonClasses.flexBetween} p-4 border border-gray-200 dark:border-gray-700 rounded-lg max-sm:flex-col max-sm:items-start max-sm:gap-4`}
              >
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <VerifiedIcon className="w-8 h-8 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-200">
                      Identity Verification (KYC)
                    </p>
                    <p
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      {user.kyc_status === "verified"
                        ? "Your identity has been successfully verified."
                        : user.kyc_status === "pending"
                        ? "Your KYC application is under review."
                        : "Complete KYC verification to unlock full platform features."}
                    </p>
                  </div>
                </div>
                <div className="text-right max-sm:text-left max-sm:w-full">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      user.kyc_status === "verified"
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : user.kyc_status === "pending"
                        ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                    }`}
                  >
                    {user.kyc_status?.toUpperCase() || "NOT STARTED"}
                  </span>
                  {(!user.kyc_status || user.kyc_status === "rejected") && (
                    <button
                      onClick={() => (window.location.href = "/dashboard/kyc")}
                      className={`${GlobalStyles.buttonPrimary} !text-xs mt-2 max-sm:w-full`}
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>

              {/* Verification Progress */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div
                  className={`text-center p-4 border rounded-lg ${
                    user.email_verified_at
                      ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
                      : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                  }`}
                >
                  <EmailIcon
                    className={`w-8 h-8 mx-auto mb-2 ${
                      user.email_verified_at
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    Email Verification
                  </h3>
                  <p
                    className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                  >
                    {user.email_verified_at ? "Completed" : "Required"}
                  </p>
                </div>

                <div
                  className={`text-center p-4 border rounded-lg ${
                    user.account_level === "3"
                      ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
                      : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                  }`}
                >
                  <VerifiedIcon
                    className={`w-8 h-8 mx-auto mb-2 ${
                      user.account_level === "3"
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    KYC Verification
                  </h3>
                  <p
                    className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                  >
                    {user.account_level === "3"
                      ? "Completed"
                      : "Required for Level 3"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
